import type { ChatHistoryOrderDetails } from '@choco-admin/types';
import { camelCaseToReadable, formatDateTime } from '@choco-admin/utils';
import {
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React, { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

interface OrderDetailsPanelProps {
  order: ChatHistoryOrderDetails;
}

const DetailRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-default-400">
      {label}
    </span>
    <span className="text-sm text-foreground break-words">{value || '—'}</span>
  </div>
);

const PRODUCT_COLUMNS = [
  { key: 'name', label: 'Product' },
  { key: 'unit', label: 'Unit' },
  { key: 'amount', label: 'Amount' },
];

/**
 * Expandable operational order details for an order-carrying chat message.
 * Collapsed by default; the "View order details" action reveals the full
 * context (status, supplier status, reference id, notes, sender, supplier and
 * product lines) fetched with the history query.
 */
export const OrderDetailsPanel: React.FC<OrderDetailsPanelProps> = ({
  order,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const statusLabel = order.status ? camelCaseToReadable(order.status) : '';
  const supplierStatusLabel = order.supplierStatus
    ? camelCaseToReadable(order.supplierStatus)
    : '';

  return (
    <div className="rounded-medium border border-default-200 bg-content2">
      <div className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          {statusLabel && (
            <Chip size="sm" variant="flat" color="primary">
              {statusLabel}
            </Chip>
          )}
          {supplierStatusLabel && (
            <Chip size="sm" variant="flat" color="secondary">
              Supplier: {supplierStatusLabel}
            </Chip>
          )}
        </div>

        <Button
          size="sm"
          variant="light"
          color="primary"
          onPress={() => setIsExpanded((previous) => !previous)}
          endContent={
            isExpanded ? (
              <FiChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <FiChevronDown className="h-4 w-4" aria-hidden />
            )
          }
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Hide order details' : 'View order details'}
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-4 px-3 pb-3">
          <Divider />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailRow label="Reference ID" value={order.referenceId} />
            <DetailRow label="Placed" value={formatDateTime(order.createdAt)} />
            <DetailRow label="Order Status" value={statusLabel} />
            <DetailRow label="Supplier Status" value={supplierStatusLabel} />
            <DetailRow label="Sender" value={order.sender} />
            <DetailRow label="Supplier" value={order.supplier} />
          </div>

          <DetailRow label="Notes" value={order.notes} />

          <div className="space-y-2">
            <span className="text-xs uppercase tracking-wide text-default-400">
              Products
            </span>
            <Table
              aria-label="Order products"
              removeWrapper
              classNames={{ table: 'min-w-[320px]' }}
            >
              <TableHeader columns={PRODUCT_COLUMNS}>
                {(column) => (
                  <TableColumn key={column.key}>{column.label}</TableColumn>
                )}
              </TableHeader>
              <TableBody
                items={order.products}
                emptyContent="No products on this order"
              >
                {(product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {product.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-default-600">
                        {product.unit || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-default-600">
                        {product.amount}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
